import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import {
  normaliseTermsBodyFormat,
  termsBodyToHtml,
  termsBodyToPlainText,
} from '@techgitter/contracts';
import type {
  QuoteDetail,
  QuoteItem,
  QuoteTermsTemplate,
  QuoteType,
  TermsBodyFormat,
} from '@techgitter/contracts';

import { useAppSettings } from '../../hooks/useAppSettings';
import { useQuoteTermsTemplates } from '../../hooks/useQuoteTermsTemplates';
import { renderQuoteTemplate } from '../../utils/quoteTemplateRenderer';
import { optionQty, optionLineSellDisplay } from '../../utils/quotePricing';
import {
  quoteCustomerTotals,
  round2,
  sumMarkedTotal,
  sumRepairServicesTotal,
  toDisplayPrice,
} from '../../utils/quoteCustomerTotals';
import { applyEft } from '../../utils/quoteEftPricing';
import {
  resolveBillingGroups,
  groupLineName,
  groupLineDescription,
  groupLineTotal,
  isEffectivelyConsolidated,
} from '../../utils/quoteBillingGroups';
import { repairServiceBlurb } from '../../utils/repairServiceBlurb';

const CONSOLIDATED_LINE_LABEL: Record<QuoteType, string> = {
  new_laptop: 'New Laptop',
  new_desktop: 'New Desktop',
  new_desktop_custom: 'New Desktop (Custom)',
  new_all_in_one: 'New All-in-One',
  repair: 'Repair Service',
  network_install: 'Network Installation',
  m365: 'M365 Onboarding',
  replacement: 'New Computer',
};

/**
 * Last-resort T&C/closer used when no T&C templates exist in the
 * catalog yet (fresh install pre-seed). Once an admin creates at
 * least one template + marks it default, this is never seen.
 */
const FALLBACK_QUOTE_TERMS =
  'We require payment upfront before we place orders with suppliers – ' +
  'please let me know if you’d like to go ahead and I will issue ' +
  'an invoice which will have bank account payment instructions, as ' +
  'well as a link for online payment (merchant fees apply).';

const FALLBACK_QUOTE_CLOSER =
  'Let me know what you think, or if you have any questions.';

function resolveTemplate(
  templates: QuoteTermsTemplate[],
  termsTemplateId: string | null,
): { body: string; closer: string; bodyFormat: TermsBodyFormat } {
  // Prefer an explicit assignment; fall back to the catalog's current
  // default; final fallback is the hardcoded boilerplate (only seen
  // when the catalog is empty entirely).
  if (termsTemplateId) {
    const hit = templates.find((t) => t.slug === termsTemplateId);
    if (hit) {
      return {
        body: hit.body,
        closer: hit.closer ?? '',
        bodyFormat: normaliseTermsBodyFormat(hit.bodyFormat),
      };
    }
  }
  const def = templates.find((t) => t.isDefault) || templates[0];
  if (def) {
    return {
      body: def.body,
      closer: def.closer ?? '',
      bodyFormat: normaliseTermsBodyFormat(def.bodyFormat),
    };
  }
  // The pre-seed boilerplate is plain prose, so it renders as prose.
  return {
    body: FALLBACK_QUOTE_TERMS,
    closer: FALLBACK_QUOTE_CLOSER,
    bodyFormat: 'text',
  };
}

/**
 * Standalone parts (product lines flagged `isStandalonePart`) sold alongside
 * the repair — e.g. a 1TB HDD on a data-recovery job. Returns one
 * `{ description, price }` per part, price in the quote's display mode, sorted
 * by position. These are itemised as their own lines in the customer preview
 * (and each becomes its own invoice line on promotion). Empty when the quote
 * has no standalone parts.
 */
const standalonePartLines = (
  quote: QuoteDetail,
  mode: 'ex' | 'inc',
): { description: string; price: number }[] => {
  return [...(quote.repairServices ?? [])]
    .filter((svc) => svc.isStandalonePart)
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map((svc) => {
      const sellExGst = (svc.components ?? []).reduce(
        (sum, c) => sum + (Number.isFinite(c.sellPrice) ? c.sellPrice : 0),
        0,
      );
      return {
        description:
          svc.description || svc.repairServiceName || svc.repairServiceCode,
        price: applyEft(
          toDisplayPrice(sellExGst, mode),
          quote.includeEftFee,
          quote.eftFeeRate,
        ),
      };
    });
};

/**
 * Newline-joined customer-facing description for the repair services
 * on a quote, in display order. Falls back to the service's name
 * snapshot when no per-service description was authored, followed by its
 * blurb (`repairServiceBlurb` — the same text its invoice line carries). Skips entries that are byte-identical to a
 * string already in the consolidated description (a fixed-price service
 * that synced its description back to svc.description and to the
 * consolidated field via the editor would otherwise show twice).
 */
export const buildRepairServicesDescriptionText = (
  quote: QuoteDetail,
  alreadyIncluded: string,
): string => {
  const seen = new Set(
    alreadyIncluded
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l.length > 0),
  );
  const sorted = [...(quote.repairServices ?? [])].sort(
    (a, b) => (a.position ?? 0) - (b.position ?? 0),
  );
  const parts: string[] = [];
  for (const svc of sorted) {
    // Standalone parts are plain product lines, not repair services — they
    // render as their own itemised priced lines, not in this write-up.
    if (svc.isStandalonePart) continue;
    const text = (svc.description || svc.repairServiceName || '').trim();
    if (!text) continue;
    if (seen.has(text)) continue;
    parts.push(repairServiceBlurb(svc, text));
    seen.add(text);
  }
  return parts.join('\n');
};

const buildAutoConsolidatedDescription = (items: QuoteItem[]): string => {
  const sorted = [...items].sort(
    (a, b) => (a.position ?? 0) - (b.position ?? 0),
  );
  const parts: string[] = [];
  for (const item of sorted) {
    if (!item.markedForInvoice) continue;
    const opt = item.options.find((o) => o.id === item.selectedOptionId);
    const desc = (opt?.description || '').trim();
    if (desc) parts.push(desc);
  }
  return parts.join('\n');
};

interface QuoteHtmlPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  quote: QuoteDetail;
}

const fmt = (n: number): string =>
  n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });

/**
 * Price-column geometry, shared by every priced row in the quote.
 *
 * Each block — item, billing group, consolidated heading, standalone part,
 * the diagnostics credit and the totals ledger — is its own table, so left
 * to size themselves the amount cells end up as wide as whatever price that
 * row happens to hold, and the description/price divider zig-zags down the
 * quote. The column is therefore STATIC: one declared width, honoured
 * literally by `table-layout:fixed`, on every priced table. Auto layout only
 * treats a declared cell width as a suggestion and reflows it against the
 * row's own content, which is what left the columns not-quite-square.
 *
 * `box-sizing:border-box` makes the 120px the whole cell in the preview and
 * in a pasted email alike (Word's table model measures cells that way too),
 * so the itemised cells can carry the padding that right-insets their digits
 * by the same 13px the bordered cards inset theirs (1px border + 12px
 * padding) without falling out of the column.
 */
const PRICE_TABLE_LAYOUT = 'table-layout:fixed;';
const PRICE_CELL_WIDTH = 'box-sizing:border-box;width:120px;';
const PRICE_CELL_PADDING = 'padding:6px 12px 6px 6px;';

const WEEKDAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_ABBR = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/**
 * Format the quote's `validUntil` (stored as ISO "YYYY-MM-DD") for the
 * customer-facing preview. Renders as e.g. `Sat 06 Jun 2026` — weekday
 * + zero-padded day + abbreviated month + 4-digit year. Falls back to
 * the raw string when the input doesn't match the expected shape so
 * malformed values still render *something*. Built without `Intl`
 * formatters so the output is deterministic regardless of the user's
 * locale.
 */
const formatValidUntil = (iso: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const year = Number(m[1]);
  const monthIdx = Number(m[2]) - 1;
  const day = Number(m[3]);
  // Construct a local-time Date so the weekday isn't shifted by a UTC
  // midnight crossing the wrong side of the IDL.
  const d = new Date(year, monthIdx, day);
  if (Number.isNaN(d.getTime())) return iso;
  return `${WEEKDAY_ABBR[d.getDay()]} ${String(day).padStart(2, '0')} ${MONTH_ABBR[monthIdx]} ${year}`;
};

/**
 * Renders the quote as styled HTML the user can copy into Outlook.
 *
 * The dialog shows a live preview; the "Copy" button writes both rich-HTML and
 * plain-text variants to the clipboard so paste targets that strip HTML still
 * receive the content.
 */
export const QuoteHtmlPreviewDialog: React.FC<QuoteHtmlPreviewDialogProps> = ({
  open,
  onClose,
  quote,
}) => {
  const previewRef = useRef<HTMLDivElement | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const { settings: appSettings, isLoading: settingsLoading } =
    useAppSettings();
  const { templates, isLoading: templatesLoading } = useQuoteTermsTemplates();

  const interpolated = useMemo(() => {
    const { body, closer, bodyFormat } = resolveTemplate(
      templates,
      quote.termsTemplateId,
    );
    return {
      body: renderQuoteTemplate(body, { appSettings, quote }),
      closer: renderQuoteTemplate(closer, { appSettings, quote }),
      bodyFormat,
    };
  }, [templates, quote, appSettings]);

  const html = useMemo(
    () => buildQuoteHtml(quote, interpolated),
    [quote, interpolated],
  );
  const plainText = useMemo(
    () => buildQuotePlainText(quote, interpolated),
    [quote, interpolated],
  );

  const stillLoading = settingsLoading || templatesLoading;

  const handleCopy = async () => {
    try {
      if (
        typeof window !== 'undefined' &&
        window.ClipboardItem &&
        navigator.clipboard?.write
      ) {
        const item = new window.ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([plainText], { type: 'text/plain' }),
        });
        await navigator.clipboard.write([item]);
      } else {
        await navigator.clipboard.writeText(plainText);
      }
      setFeedback('Copied to clipboard — paste into Outlook.');
    } catch (err) {
      setFeedback(
        `Copy failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Quote preview ({quote.number})</DialogTitle>
      <DialogContent dividers>
        {feedback && (
          <Alert
            severity={feedback.startsWith('Copy failed') ? 'error' : 'success'}
            sx={{ mb: 2 }}
            onClose={() => setFeedback(null)}
          >
            {feedback}
          </Alert>
        )}
        {stillLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            ref={previewRef}
            sx={{ border: '1px solid', borderColor: 'divider', p: 2, bgcolor: 'background.paper' }}
            // Render the same HTML that gets copied so the preview is faithful.
            dangerouslySetInnerHTML={{ __html: html }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={handleCopy}>
          Copy for Outlook
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const escapeHtml = (s: string): string =>
  s.replace(/[&<>"']/g, (ch) => {
    switch (ch) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      default: return '&#39;';
    }
  });

interface RenderedTerms {
  /** Interpolated T&C body — markdown source, or prose with `\n` breaks. */
  body: string;
  /** Interpolated closer line; may be empty if the template omits one. */
  closer: string;
  /** How the template says to render the two above. */
  bodyFormat: TermsBodyFormat;
}

function buildQuoteHtml(quote: QuoteDetail, terms: RenderedTerms): string {
  const mode = quote.priceDisplayMode ?? 'ex';
  const customerLine = escapeHtml(
    quote.customerBusinessName || quote.customerFullName || quote.customerName || '',
  );
  const validUntil = quote.validUntil
    ? `Valid until ${escapeHtml(formatValidUntil(quote.validUntil))}`
    : '';
  const gstTag = mode === 'inc' ? 'All prices inc GST' : 'All prices ex GST';

  const jobSummaryHtml = buildJobSummaryHtml(quote);

  const itemsHtml = isEffectivelyConsolidated(quote)
    ? buildConsolidatedHtml(quote, mode)
    : buildItemisedHtml(quote, mode);

  const diagReimbursementHtml = buildDiagReimbursementHtml(quote, mode);
  const summaryHtml = buildSummaryHtml(quote, mode);

  const note = quote.note
    ? `<p style="white-space:pre-wrap;margin-top:16px;">${escapeHtml(quote.note)}</p>`
    : '';

  // `text` escapes and turns author line breaks into `<br />`, exactly as
  // before; `markdown` goes through the restricted subset, which emits inline
  // styles because this HTML is pasted into Outlook. Raw HTML in the body is
  // escaped either way — the format decides structure, never trust.
  const bodyHtml = termsBodyToHtml(terms.body, terms.bodyFormat);
  const closerHtml = termsBodyToHtml(terms.closer, terms.bodyFormat);

  const termsHtml = terms.body
    ? `
    <div style="margin-top:20px;padding-top:12px;border-top:1px solid #eee;
                color:#444;font-size:12px;">
      <div style="font-weight:bold;color:#555;margin-bottom:6px;">
        Terms &amp; Conditions
      </div>
      ${
        terms.bodyFormat === 'markdown'
          ? bodyHtml
          : `<p style="margin:0 0 12px 0;">${bodyHtml}</p>`
      }
    </div>${
      closerHtml
        ? terms.bodyFormat === 'markdown'
          ? `\n    <div style="margin:12px 0 0 0;color:#333;">${closerHtml}</div>`
          : `\n    <p style="margin:12px 0 0 0;color:#333;">${closerHtml}</p>`
        : ''
    }`
    : '';

  // Heading = "Quote <number>: <name>" when named, else "Quote <number>".
  const quoteName = (quote.name || '').trim();
  const headingHtml = quoteName
    ? `Quote ${escapeHtml(quote.number)}: ${escapeHtml(quoteName)}`
    : `Quote ${escapeHtml(quote.number)}`;
  const subtitle = [customerLine, gstTag].filter(Boolean).join(' · ');

  return `<div style="font-family:Arial,sans-serif;font-size:13px;line-height:1.5;">
    <h2 style="margin:0 0 4px 0;font-size:15px;font-weight:bold;color:#000;">${headingHtml}</h2>
    <div style="color:#555;margin-bottom:${validUntil ? '4px' : '12px'};">
      ${subtitle}
    </div>${
      validUntil
        ? `\n    <div style="color:#333;font-weight:bold;margin-bottom:12px;">${validUntil}</div>`
        : ''
    }
    ${jobSummaryHtml}
    ${itemsHtml}
    ${diagReimbursementHtml}
    ${summaryHtml}
    ${note}
    ${termsHtml}
  </div>`;
}

/**
 * Customer credit block for the diagnostic-fee reimbursement carried
 * over from the source check-in's invoice (or, for jobs that weren't
 * checked in, from the job's paid invoices). Sits BELOW the line
 * items so the customer reads the price first, then sees the credit
 * being subtracted. Amount on the quote is the gross customer-paid
 * amount (inc-GST); we display ex-GST or inc-GST to match the rest
 * of the quote.
 */
function buildDiagReimbursementHtml(
  quote: QuoteDetail,
  mode: 'ex' | 'inc',
): string {
  const credit = quote.diagnosticReimbursement;
  if (!credit) return '';
  const displayAmount = round2(
    mode === 'inc' ? credit.amount : credit.amount / 1.1,
  );
  const sell = fmt(-displayAmount);
  const desc = `Reimbursement of diagnostics fee paid on invoice ${credit.sourceInvoiceNumber}`;
  // Tables (not flexbox) — Outlook for Mac and most email clients strip
  // `display:flex`, which collapses the label and value into adjoining
  // text with no separator. The bordered "card" is a table with an
  // inline `padding:12px` on its single `<td>`; we explicitly avoid the
  // deprecated `cellpadding` HTML attribute because Outlook for
  // Windows and some webmails silently drop it when the same table
  // also carries a `style` attribute, which left earlier versions of
  // this dialog rendering padding-less cards. `width:100%` on every
  // nested table keeps the layout flexible at any Outlook compose-
  // window width.
  return `
    <table role="presentation" cellspacing="0" cellpadding="0" style="width:100%;border:1px solid #ccc;border-collapse:collapse;border-radius:4px;margin-bottom:16px;">
      <tr>
        <td style="padding:12px;">
          <table role="presentation" cellspacing="0" cellpadding="0" style="width:100%;border-collapse:collapse;${PRICE_TABLE_LAYOUT}font-size:14px;">
            <tr>
              <td style="text-align:left;font-weight:bold;">Diagnostics Fee Credit</td>
              <td style="text-align:right;font-weight:bold;white-space:nowrap;${PRICE_CELL_WIDTH}">${sell}</td>
            </tr>
          </table>
          <div style="color:#333;margin-top:4px;">${escapeHtml(desc)}</div>
        </td>
      </tr>
    </table>`;
}

/**
 * Total summary block — sits below the line items + credit so the
 * customer can see exactly how the bottom-line is built up:
 *   Subtotal             $X
 *   Bundle discount      -$Y     (only shown when set)
 *   Diagnostics credit   -$Z     (only shown when applicable)
 *   ─────────────────────────
 *   Total                $T
 *
 * The subtotal is the pre-discount total of the customer-facing
 * lines (repair services for repair quotes, marked items otherwise).
 * The total nets bundle-discount + diagnostics credit, matching what
 * the customer will see on the resulting invoice.
 */
function buildSummaryHtml(
  quote: QuoteDetail,
  mode: 'ex' | 'inc',
): string {
  // One ledger, shared with the plain-text variant and with the T&C template
  // renderer — a terms line quoting a different total from the Total row
  // directly above it is the failure this extraction prevents. Neither the
  // bundle discount nor the diagnostics credit is EFT-baked there: the
  // backend's bake skips every negative line.
  const bundle = quote.bundleDiscount;
  const credit = quote.diagnosticReimbursement;
  const { subtotal, bundleAmount, creditAmount, total } =
    quoteCustomerTotals(quote);

  // Each row is a 2-column table row (label · amount). Outlook strips
  // flexbox styles, so the whole ledger is one table with a bordered
  // final row for the Total — single layout primitive, renders the
  // same in Gmail, Outlook for Mac, and the web preview.
  const rowStyle = 'padding:2px 0;font-size:13px;color:#333;';
  const rows: string[] = [];
  rows.push(`
    <tr>
      <td style="text-align:left;${rowStyle}">Subtotal</td>
      <td style="text-align:right;white-space:nowrap;${PRICE_CELL_WIDTH}${rowStyle}">${fmt(subtotal)}</td>
    </tr>`);
  if (bundle && bundle.amount > 0) {
    const label = escapeHtml(bundle.description || 'Bundle discount');
    rows.push(`
    <tr>
      <td style="text-align:left;${rowStyle}">${label}</td>
      <td style="text-align:right;white-space:nowrap;${PRICE_CELL_WIDTH}${rowStyle}">${fmt(-bundleAmount)}</td>
    </tr>`);
  }
  if (credit) {
    rows.push(`
    <tr>
      <td style="text-align:left;${rowStyle}">Diagnostics Fee Credit</td>
      <td style="text-align:right;white-space:nowrap;${PRICE_CELL_WIDTH}${rowStyle}">${fmt(-creditAmount)}</td>
    </tr>`);
  }
  const totalCellStyle =
    'padding:8px 0 0 0;border-top:1px solid #ddd;font-size:15px;font-weight:bold;color:#000;';

  return `
    <table role="presentation" cellspacing="0" cellpadding="0" style="width:100%;border:1px solid #ccc;border-collapse:collapse;border-radius:4px;margin-bottom:16px;">
      <tr>
        <td style="padding:12px;">
          <table role="presentation" cellspacing="0" cellpadding="0" style="width:100%;border-collapse:collapse;${PRICE_TABLE_LAYOUT}">
            ${rows.join('')}
            <tr>
              <td style="text-align:left;${totalCellStyle}">Total</td>
              <td style="text-align:right;white-space:nowrap;${PRICE_CELL_WIDTH}${totalCellStyle}">${fmt(total)}</td>
            </tr>
          </table>
        </td>
      </tr>
    </table>`;
}

function buildItemisedHtml(quote: QuoteDetail, mode: 'ex' | 'inc'): string {
  // Billing groups collapse to one block each (name + Includes + summed price);
  // ungrouped items render normally. Grouped items are rendered at the position
  // of the group's first member; other members are skipped.
  const groups = resolveBillingGroups(quote.items);
  const groupByFirstMember = new Map(groups.map((g) => [g.items[0].id, g]));
  const groupedIds = new Set(groups.flatMap((g) => g.items.map((i) => i.id)));

  const renderItemBlock = (item: QuoteItem): string => {
    // Render only the SELECTED option (what gets billed), never unselected
    // alternatives — the customer preview must match the invoice line-for-line.
    const opt = item.options.find((o) => o.id === item.selectedOptionId);
    if (!opt) return '';
    const qty = optionQty(opt);
    const desc =
      escapeHtml(opt.description || '') +
      (qty > 1 ? ` <span style="color:#666;">(×${qty})</span>` : '');
    const sell = fmt(
      applyEft(
        optionLineSellDisplay(opt, mode),
        quote.includeEftFee,
        quote.eftFeeRate,
      ),
    );
    // Image column only when this option actually has an image — no empty
    // placeholder cell otherwise.
    const imgCell = opt.imageUrl
      ? `<td style="width:96px;padding:6px;border:1px solid #e0e0e0;vertical-align:top;"><img src="${escapeHtml(opt.imageUrl)}" alt="" style="width:80px;height:80px;object-fit:contain;display:block;" /></td>`
      : '';
    // The item name (e.g. a category label) is an internal grouping label —
    // show it as a heading only when the tech set a meaningful one.
    const nameHeader = (item.name || '').trim()
      ? `<div style="font-size:14px;font-weight:bold;margin-bottom:4px;">${escapeHtml(item.name)}</div>`
      : '';
    return `
        <div style="margin-bottom:16px;">
          ${nameHeader}
          <table style="border-collapse:collapse;width:100%;${PRICE_TABLE_LAYOUT}">
            <tbody>
              <tr>
                ${imgCell}
                <td style="padding:6px;border:1px solid #e0e0e0;vertical-align:top;">${desc}</td>
                <td style="${PRICE_CELL_PADDING}border:1px solid #e0e0e0;vertical-align:top;text-align:right;white-space:nowrap;${PRICE_CELL_WIDTH}">${sell}</td>
              </tr>
            </tbody>
          </table>
        </div>`;
  };

  const renderGroupBlock = (group: ReturnType<typeof resolveBillingGroups>[number]): string => {
    // The group NAME is the invoice line code (RS product column) — like a
    // regular line's productCode, it is NOT shown to the customer. The preview
    // shows only the group's customer-facing description + summed price.
    const desc = escapeHtml(groupLineDescription(group) || groupLineName(group));
    const price = fmt(
      applyEft(groupLineTotal(group, mode), quote.includeEftFee, quote.eftFeeRate),
    );
    return `
        <div style="margin-bottom:16px;">
          <table style="border-collapse:collapse;width:100%;${PRICE_TABLE_LAYOUT}">
            <tbody>
              <tr>
                <td style="padding:6px;border:1px solid #e0e0e0;vertical-align:top;">${desc}</td>
                <td style="${PRICE_CELL_PADDING}border:1px solid #e0e0e0;vertical-align:top;text-align:right;white-space:nowrap;${PRICE_CELL_WIDTH}">${price}</td>
              </tr>
            </tbody>
          </table>
        </div>`;
  };

  return [...quote.items]
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map((item) => {
      if (groupedIds.has(item.id)) {
        const g = groupByFirstMember.get(item.id);
        return g ? renderGroupBlock(g) : ''; // non-first members skipped
      }
      // Only render lines that will actually be billed — marked, with a
      // selected option. Skips unmarked items, undecided items, and
      // empty-category placeholders, so the preview matches the invoice.
      if (!item.markedForInvoice || !item.selectedOptionId) return '';
      return renderItemBlock(item);
    })
    .join('');
}

/**
 * Standalone "Summary of Job <number>" block for repair quotes. Shows the
 * composed job write-up (diagnostics / intake / device details, authored in
 * the JobSummaryEditor and stored in `consolidatedDescription`) as its own
 * section, above the pricing block. Empty for non-repair quotes or when no
 * summary has been authored.
 */
function buildJobSummaryHtml(quote: QuoteDetail): string {
  if (quote.type !== 'repair') return '';
  const summary = (quote.consolidatedDescription || '').trim();
  if (!summary) return '';
  const jobNo = (quote.rsTicketNumber || '').trim();
  const heading = `Summary of Job${jobNo ? ` ${escapeHtml(jobNo)}` : ''}`;
  const bodyHtml = escapeHtml(summary).replace(/\n/g, '<br />');
  return `
    <table role="presentation" cellspacing="0" cellpadding="0" style="width:100%;border:1px solid #ccc;border-collapse:collapse;border-radius:4px;margin-bottom:16px;">
      <tr>
        <td style="padding:12px;">
          <div style="font-weight:bold;color:#000;margin-bottom:6px;font-size:14px;">${heading}</div>
          <div style="white-space:pre-wrap;color:#333;">${bodyHtml}</div>
        </td>
      </tr>
    </table>`;
}

function buildConsolidatedHtml(quote: QuoteDetail, mode: 'ex' | 'inc'): string {
  // Consolidated quotes show one summary block with the marked total
  // and the editable consolidated description below it. The
  // description prefers the saved override; falls back to the
  // auto-built version (so the preview is faithful even when nothing
  // has been saved yet) and finally to the type-default label.
  //
  // Repair quotes draw their total from `quote.repairServices` (each
  // service's components sum to the customer-facing price). Other
  // types still use the marked-items total.
  const isServiceShaped =
    quote.type === 'repair' || quote.type === 'network_install';
  const total = isServiceShaped
    ? sumRepairServicesTotal(quote, mode)
    : sumMarkedTotal(quote, mode);
  let description: string;
  if (quote.type === 'repair') {
    // The job write-up now renders in its own "Summary of Job" section
    // (buildJobSummaryHtml), so the repair pricing block lists only the
    // per-service "what we propose to do" descriptions.
    description =
      buildRepairServicesDescriptionText(quote, '') ||
      CONSOLIDATED_LINE_LABEL[quote.type];
  } else {
    description =
      (quote.consolidatedDescription || '').trim() ||
      buildAutoConsolidatedDescription(quote.items) ||
      CONSOLIDATED_LINE_LABEL[quote.type];
    // network_install (service-shaped) still appends each repair-service's
    // customer-facing description below the consolidated text.
    if (isServiceShaped) {
      const repairText = buildRepairServicesDescriptionText(quote, description);
      if (repairText) {
        description = `${description}\n\n${repairText}`;
      }
    }
  }
  const heading = escapeHtml(CONSOLIDATED_LINE_LABEL[quote.type]);
  // Preserve newlines in the customer-facing description.
  const descHtml = escapeHtml(description).replace(/\n/g, '<br />');
  // Standalone parts render as their own priced rows under the consolidated
  // service line, so the customer sees each itemised (they bill as separate
  // invoice lines too). The heading total above already includes them via
  // sumRepairServicesTotal.
  const standalone = standalonePartLines(quote, mode);
  const standaloneRows = standalone
    .map(
      (p) => `
            <tr>
              <td style="text-align:left;padding:2px 0;">${escapeHtml(p.description)}</td>
              <td style="text-align:right;white-space:nowrap;padding:2px 0;${PRICE_CELL_WIDTH}">${fmt(p.price)}</td>
            </tr>`,
    )
    .join('');
  const standaloneHtml = standalone.length
    ? `
          <table role="presentation" cellspacing="0" cellpadding="0" style="width:100%;border-collapse:collapse;${PRICE_TABLE_LAYOUT}font-size:14px;margin-top:8px;">
            ${standaloneRows}
          </table>`
    : '';
  return `
    <table role="presentation" cellspacing="0" cellpadding="0" style="width:100%;border:1px solid #ccc;border-collapse:collapse;border-radius:4px;margin-bottom:16px;">
      <tr>
        <td style="padding:12px;">
          <table role="presentation" cellspacing="0" cellpadding="0" style="width:100%;border-collapse:collapse;${PRICE_TABLE_LAYOUT}font-size:14px;margin-bottom:8px;">
            <tr>
              <td style="text-align:left;font-weight:bold;">${heading}</td>
              <td style="text-align:right;font-weight:bold;white-space:nowrap;${PRICE_CELL_WIDTH}">${fmt(total)}</td>
            </tr>
          </table>
          <div style="white-space:pre-wrap;color:#333;">${descHtml}</div>${standaloneHtml}
        </td>
      </tr>
    </table>`;
}

function buildQuotePlainText(quote: QuoteDetail, terms: RenderedTerms): string {
  const mode = quote.priceDisplayMode ?? 'ex';
  const gstTag = mode === 'inc' ? 'All prices inc GST' : 'All prices ex GST';
  const isServiceShaped =
    quote.type === 'repair' || quote.type === 'network_install';
  const lines: string[] = [];
  const quoteName = (quote.name || '').trim();
  lines.push(
    quoteName ? `Quote ${quote.number}: ${quoteName}` : `Quote ${quote.number}`,
  );
  lines.push(
    quote.customerBusinessName || quote.customerFullName || quote.customerName || '',
  );
  if (quote.validUntil) lines.push(`Valid until ${formatValidUntil(quote.validUntil)}`);
  lines.push(gstTag);
  lines.push('');
  // Standalone job summary section for repair quotes (mirrors the HTML
  // "Summary of Job" block), above the pricing lines.
  if (quote.type === 'repair') {
    const summary = (quote.consolidatedDescription || '').trim();
    if (summary) {
      const jobNo = (quote.rsTicketNumber || '').trim();
      lines.push(`Summary of Job${jobNo ? ` ${jobNo}` : ''}`);
      for (const l of summary.split('\n')) lines.push(`  ${l}`);
      lines.push('');
    }
  }
  // Lines first — repair service / items above the credit so the
  // customer reads the offer, then the credit, then the total.
  if (isEffectivelyConsolidated(quote)) {
    const total = isServiceShaped
      ? sumRepairServicesTotal(quote, mode)
      : sumMarkedTotal(quote, mode);
    const heading = CONSOLIDATED_LINE_LABEL[quote.type];
    let description: string;
    if (quote.type === 'repair') {
      // Job write-up shown separately above; here list only the
      // per-service descriptions.
      description = buildRepairServicesDescriptionText(quote, '') || heading;
    } else {
      description =
        (quote.consolidatedDescription || '').trim() ||
        buildAutoConsolidatedDescription(quote.items) ||
        heading;
      if (isServiceShaped) {
        const repairText = buildRepairServicesDescriptionText(quote, description);
        if (repairText) {
          description = `${description}\n\n${repairText}`;
        }
      }
    }
    lines.push(`${heading} — ${fmt(total)}`);
    for (const descLine of description.split('\n')) {
      lines.push(`  ${descLine}`);
    }
    // Standalone parts as their own itemised lines (mirrors the HTML block).
    for (const part of standalonePartLines(quote, mode)) {
      lines.push(`${part.description} — ${fmt(part.price)}`);
    }
    lines.push('');
  } else {
    // Itemised — billing groups collapse to one line each (name + Includes +
    // summed price), ungrouped items render normally, in position order.
    const groups = resolveBillingGroups(quote.items);
    const groupByFirstMember = new Map(groups.map((g) => [g.items[0].id, g]));
    const groupedIds = new Set(groups.flatMap((g) => g.items.map((i) => i.id)));
    const sortedItems = [...quote.items].sort(
      (a, b) => (a.position ?? 0) - (b.position ?? 0),
    );
    for (const item of sortedItems) {
      if (groupedIds.has(item.id)) {
        const g = groupByFirstMember.get(item.id);
        if (!g) continue; // non-first member
        const price = fmt(
          applyEft(
            groupLineTotal(g, mode),
            quote.includeEftFee,
            quote.eftFeeRate,
          ),
        );
        // Group NAME is the invoice line code — not customer-facing; show the
        // description (falling back to the name only if there's no description).
        lines.push(`${groupLineDescription(g) || groupLineName(g)} — ${price}`);
        lines.push('');
        continue;
      }
      // Only billed lines: marked, with a selected option; render just that
      // option (never unselected alternatives), matching the invoice.
      if (!item.markedForInvoice || !item.selectedOptionId) continue;
      const opt = item.options.find((o) => o.id === item.selectedOptionId);
      if (!opt) continue;
      const sell = fmt(
        applyEft(
          optionLineSellDisplay(opt, mode),
          quote.includeEftFee,
          quote.eftFeeRate,
        ),
      );
      if ((item.name || '').trim()) lines.push(`${item.name}`);
      lines.push(`  - ${opt.description} — ${sell}`);
      lines.push('');
    }
  }
  const credit = quote.diagnosticReimbursement;
  if (credit) {
    // No EFT uplift — the credit refunds exactly what was paid.
    const displayAmount = round2(
      mode === 'inc' ? credit.amount : credit.amount / 1.1,
    );
    lines.push(`Diagnostics Fee Credit — ${fmt(-displayAmount)}`);
    lines.push(
      `  Reimbursement of diagnostics fee paid on invoice ${credit.sourceInvoiceNumber}`,
    );
    lines.push('');
  }
  // Summary — the same ledger the HTML variant renders.
  const bundle = quote.bundleDiscount;
  const { subtotal, bundleAmount, creditAmount, total } =
    quoteCustomerTotals(quote);
  lines.push(`Subtotal — ${fmt(subtotal)}`);
  if (bundle && bundle.amount > 0) {
    lines.push(`${bundle.description || 'Bundle discount'} — ${fmt(-bundleAmount)}`);
  }
  if (credit) {
    lines.push(`Diagnostics Fee Credit — ${fmt(-creditAmount)}`);
  }
  lines.push(`Total — ${fmt(total)}`);
  lines.push('');
  if (quote.note) {
    lines.push(quote.note);
    lines.push('');
  }
  if (terms.body) {
    lines.push('Terms & Conditions');
    // Markdown flattens to readable text — bullets keep their dashes, links
    // keep their URLs — so the `text/plain` flavour of the clipboard is not a
    // page of asterisks.
    lines.push(termsBodyToPlainText(terms.body, terms.bodyFormat));
    if (terms.closer) {
      lines.push('');
      lines.push(termsBodyToPlainText(terms.closer, terms.bodyFormat));
    }
  }
  return lines.join('\n');
}
